import { goToPlace, isEditing, leaveEditor, placeOf } from '@daily/ui';
import type { AppStore, Place } from '@daily/ui';
import { keptSearch, pathOf, placeFromPath } from './address.js';

/** Which way the browser moved through its history, when it says. */
export type Traverse = 'back' | 'forward' | 'unknown';

/** The browser's address and history, as the address bar needs them. */
export interface AddressBar {
  pathname(): string;
  search(): string;
  push(href: string): void;
  replace(href: string): void;
  back(): void;
  forward(): void;
  /** Back, Forward or a jump through the browser's history (not the app's own push and replace). */
  onTraverse(listener: (direction: Traverse) => void): () => void;
}

/** What the address bar uses of TanStack Router's history (`router.history`). */
export interface RouterHistoryLike {
  readonly location: { readonly pathname: string; readonly search: string };
  push(path: string): void;
  replace(path: string): void;
  back(): void;
  forward(): void;
  subscribe(listener: (event: { readonly action: { readonly type: string } }) => void): () => void;
}

/** The router's history: the router learns of every change, and says which way the browser moved. */
export function routerAddressBar(history: RouterHistoryLike): AddressBar {
  return {
    pathname: () => history.location.pathname, search: () => history.location.search,
    push: (href) => history.push(href), replace: (href) => history.replace(href),
    back: () => history.back(), forward: () => history.forward(),
    onTraverse: (listener) => history.subscribe(({ action }) => {
      if (action.type === 'BACK') listener('back');
      else if (action.type === 'FORWARD') listener('forward');
      else if (action.type === 'GO') listener('unknown');
    }),
  };
}

/** The window's own history, where no router runs (unit tests). */
export function windowAddressBar(win: Window = window): AddressBar {
  return {
    pathname: () => win.location.pathname, search: () => win.location.search,
    push: (href) => win.history.pushState(null, '', href), replace: (href) => win.history.replaceState(win.history.state, '', href),
    back: () => win.history.back(), forward: () => win.history.forward(),
    onTraverse: (listener) => {
      const popped = () => listener('unknown');
      win.addEventListener('popstate', popped);
      return () => win.removeEventListener('popstate', popped);
    },
  };
}

type Memory = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
/** This tab's session storage, or nothing where it cannot be used (a private window, blocked site data). */
function tabMemory(): Memory | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
}
const NEXT = 'kalndar.next';
const NEXT_MS = 30 * 60_000;

const connected = new WeakSet<AppStore>();

/**
 * Keeps the address and the screen in step (owner, 4 October 2026):
 *
 * - Signed in, every page the screen settles on gets its address: a new history entry for a page the
 *   person went to, the same entry corrected when the address already moved (Back, Forward, a link).
 * - An address opened or reloaded shows its page once the app is ready. `/` signed in is `/home`; an
 *   address that names no page is `/home` too.
 * - Back and Forward show the page of the address, with the app's own steps where one leads there.
 * - The composer and the profile editor have no address: Back or Forward there is their Cancel (it asks
 *   first when something changed), and the address stays on their page.
 * - Signed out, a page address stays (sign in, then that page shows). Sign-in with Google always comes
 *   back to `/`, so the page waiting for sign-in is remembered in this tab for half an hour.
 * - Signing out goes to `/`. The password-reset address is left to its own handling.
 *
 * Only the fixture switches of the query are kept (`keptSearch`). Connects once per store.
 */
export function connectAddressBar(store: AppStore, bar: AddressBar, memory: Memory | null = tabMemory(), now: () => number = Date.now): () => void {
  if (connected.has(store)) return () => undefined;
  connected.add(store);
  let inside = false;
  let navigating = 0;
  let fromHistory = false;
  let restoring = false;
  let waiting: string | null = null;
  /** The address of the last page the screen showed: where an editor's page is. */
  let shown: string | null = null;
  const href = (path: string) => `${path}${keptSearch(bar.search())}`;
  const resetting = () => bar.pathname() === '/reset-password';

  const remember = (path: string) => {
    if (waiting === path) return;
    waiting = path;
    try { memory?.setItem(NEXT, JSON.stringify({ path, at: now() })); } catch { /* nothing remembered */ }
  };
  const recall = (): string | null => {
    try {
      const raw = memory?.getItem(NEXT) ?? null;
      memory?.removeItem(NEXT);
      const saved = raw ? JSON.parse(raw) as { path?: unknown; at?: unknown } : null;
      return saved && typeof saved.path === 'string' && typeof saved.at === 'number' && now() - saved.at < NEXT_MS ? saved.path : null;
    } catch { return null; }
  };

  /** The page on screen gets its address. */
  const sync = () => {
    if (navigating || !inside || resetting()) return;
    const place = placeOf(store.getState());
    if (!place) return;
    const path = pathOf(place);
    if (path !== bar.pathname()) { if (fromHistory) bar.replace(href(path)); else bar.push(href(path)); }
    shown = path;
    fromHistory = false;
  };
  const go = (place: Place, animate: boolean) => {
    navigating += 1;
    void goToPlace(store, place, animate).finally(() => { navigating -= 1; sync(); });
  };
  /** Signed in and ready: the page of the address. */
  const arrive = () => {
    const opened = placeFromPath(bar.pathname());
    // Whatever waited for sign-in is read once; it only counts when the app opened on `/` (the Google return).
    const saved = recall();
    const target = opened === 'landing' && saved ? placeFromPath(saved) : opened;
    fromHistory = true;
    if (target === null || target === 'landing') { bar.replace(href('/home')); fromHistory = false; return; }
    if (target !== opened) bar.replace(href(pathOf(target)));
    go(target, false);
  };

  const unsubscribe = store.subscribe(() => {
    const st = store.getState();
    if (resetting()) return;
    if (st.boot === 'ready') {
      if (!inside) { inside = true; waiting = null; arrive(); return; }
      sync();
      return;
    }
    if (st.boot === 'signed-out') {
      if (inside) { inside = false; bar.replace(href('/')); return; }
      const path = bar.pathname(), place = placeFromPath(path);
      if (place && place !== 'landing') remember(path);
    }
  });
  const unlisten = bar.onTraverse((direction) => {
    if (restoring) { restoring = false; return; }
    if (!inside || resetting()) return;
    if (isEditing(store.getState())) {
      // Undo the move, so the address stays on the editor's page, and cancel as the island's Cancel would.
      restoring = direction !== 'unknown';
      if (direction === 'back') bar.forward();
      else if (direction === 'forward') bar.back();
      else if (shown) bar.push(href(shown));
      leaveEditor(store);
      return;
    }
    const target = placeFromPath(bar.pathname());
    fromHistory = true;
    go(target === null || target === 'landing' ? { kind: 'home' } : target, true);
  });
  return () => { unsubscribe(); unlisten(); connected.delete(store); };
}
